// src/components/CashflowPanel.jsx
import React, { memo, useRef } from 'react';
import { Box, Button, Select, MenuItem, Typography, CircularProgress, FormControl, InputLabel, Chip, useTheme, alpha, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { PamGrid } from '@1823-partners/core';
import { orderCashflowFields, isCouponField, formatCouponPercent } from '../utils/cashflowDisplay';

const CashflowPanel = memo(({
  cashflowData,
  cashflowMonthlyData = [], // one-row-per-period rows, keyed by DATE instead of YEAR
  tranches,             // array of all tranches sent to API
  selectedTranche,      // single tranche to show
  onTrancheChange,      // handler to pick a tranche
  grain = 'year',        // 'year' | 'month'
  onGrainChange = () => {},
  loading = false
}) => {
  const theme = useTheme();
  const gridRef = useRef(null);

  // The monthly rows (when already available) let the toggle switch instantly
  // with no refetch; the stored-data path refetches by grain in the parent.
  const activeCashflowData = grain === 'month' ? cashflowMonthlyData : cashflowData;

  // Filter cashflow data by selected tranche
  const filteredCashflowData = React.useMemo(() => {
    if (!activeCashflowData?.length) {
      return [];
    }

    // If no tranche selected, show all data
    if (!selectedTranche) {
      return activeCashflowData;
    }

    // Filter data to only show rows for the selected tranche
    return activeCashflowData.filter(row => {
      const trancheValue = row.TRANCHE || row.Tranche || row.tranche;
      return trancheValue === selectedTranche;
    });
  }, [activeCashflowData, selectedTranche]);

  // Calculate height based on filtered data
  const tableHeight = React.useMemo(() => {
    const rowHeight = 32;
    const headerHeight = 36;
    const controlsHeight = 60;
    const padding = 20;
    const minHeight = 300; // Increased minimum height
    const maxHeight = 500; // Increased maximum height

    const dataRows = filteredCashflowData?.length || 0;
    const calculatedHeight = headerHeight + (Math.min(dataRows, 15) * rowHeight) + controlsHeight + padding;

    return Math.max(minHeight, Math.min(calculatedHeight, maxHeight));
  }, [filteredCashflowData]);

  const colDefs = React.useMemo(
    () =>
      filteredCashflowData?.length
        ? orderCashflowFields(filteredCashflowData).map((field, index) => {
          const isDateColumn = field === 'ASOFDT' || field.toLowerCase().includes('date');
          return {
            headerName: field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
            field,
            width: index === 0 ? 150 : 120, // First column wider, others fixed width
            minWidth: 100,
            maxWidth: 200,
            resizable: true,
            sortable: true,
            filter: isDateColumn ? 'agDateColumnFilter' : true,
            ...(isDateColumn && {
              filterParams: {
                comparator: (filterDate, cellValue) => {
                  if (!cellValue) return -1;

                  let cellDate;

                  // Handle Date object
                  if (cellValue instanceof Date) {
                    cellDate = new Date(cellValue);
                  }
                  // Handle ISO string dates (YYYY-MM-DD)
                  else if (typeof cellValue === 'string' && cellValue.match(/^\d{4}-\d{2}-\d{2}/)) {
                    cellDate = new Date(cellValue);
                  }
                  // Handle numeric dates (YYYYMMDD format)
                  else if (typeof cellValue === 'number' || (typeof cellValue === 'string' && cellValue.match(/^\d{8}$/))) {
                    const str = String(cellValue);
                    if (str.length === 8) {
                      const year = parseInt(str.slice(0, 4));
                      const month = parseInt(str.slice(4, 6)) - 1;
                      const day = parseInt(str.slice(6, 8));
                      cellDate = new Date(year, month, day);
                    }
                  }
                  // Handle other date strings
                  else if (typeof cellValue === 'string') {
                    cellDate = new Date(cellValue);
                  }

                  if (!cellDate || isNaN(cellDate.getTime())) return 0;

                  cellDate.setHours(0, 0, 0, 0);
                  filterDate.setHours(0, 0, 0, 0);

                  if (cellDate < filterDate) return -1;
                  if (cellDate > filterDate) return 1;
                  return 0;
                }
              }
            }),
            valueFormatter: ({ value }) => {
              if (value === null || value === undefined) return '';
              if (typeof value === 'number' && !isNaN(value)) {
                // Format YEAR column as plain integer without commas
                if (field.toLowerCase() === 'year') {
                  return Math.round(value).toString();
                }
                // COUPON is the effective coupon rate in percent, not a dollar
                // amount - render it as a percentage, never with comma grouping.
                if (isCouponField(field)) {
                  return formatCouponPercent(value);
                }
                // Format other numeric columns with 2 decimal places and commas
                return value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
              }
              return value;
            }
          };
        })
        : [],
    [filteredCashflowData]
  );

  // Handle export via PamGrid ref
  const handleExport = () => {
    if (gridRef.current?.api) {
      gridRef.current.api.exportDataAsExcel({
        fileName: `cashflow${selectedTranche ? `_${selectedTranche}` : ''}.xlsx`,
        sheetName: 'Cashflows',
      });
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {/* Professional Controls Header */}
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="subtitle2"
          sx={{
            color: theme.palette.text.secondary,
            fontSize: '12px',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            mb: 1
          }}
        >
          Configuration
        </Typography>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box display="flex" alignItems="center" gap={2}>
            <FormControl
              size="small"
              sx={{
                minWidth: 200,
                '& .MuiOutlinedInput-root': {
                  bgcolor: theme.palette.background.paper,
                  borderRadius: 1.5,
                  '&.Mui-focused': {
                    borderColor: theme.palette.primary.main,
                    boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`
                  }
                }
              }}
            >
              <InputLabel sx={{ fontWeight: 500 }}>Select Tranche</InputLabel>
              <Select
                size="small"
                value={selectedTranche}
                onChange={e => onTrancheChange(e.target.value)}
                label="Select Tranche"
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Chip
                      label={selected}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '11px',
                        bgcolor: alpha(theme.palette.error.main, 0.15),
                        color: theme.palette.error.dark,
                        fontWeight: 600
                      }}
                    />
                  </Box>
                )}
              >
                {tranches.map(t => (
                  <MenuItem key={t} value={t}>{t}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={grain}
              onChange={(_, value) => value && onGrainChange(value)}
              aria-label="Cashflow grain"
            >
              <ToggleButton value="month">Monthly</ToggleButton>
              <ToggleButton value="year">Yearly</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <Button
            size="small"
            variant="contained"
            color="primary"
            disabled={!filteredCashflowData || filteredCashflowData.length === 0}
            onClick={handleExport}
            sx={{
              fontWeight: 600,
              borderRadius: 2,
              px: 2,
              py: 1,
              textTransform: 'none',
              fontSize: '13px',
              transition: 'all 0.2s ease'
            }}
          >
            Export
          </Button>
        </Box>
      </Box>
      <Box sx={{ height: `${tableHeight}px`, position: 'relative' }}>
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: alpha(theme.palette.background.paper, 0.8),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
            }}
          >
            <CircularProgress color="primary" />
          </Box>
        )}
        <PamGrid
          ref={gridRef}
          rowData={filteredCashflowData}
          columnDefs={colDefs}
          defaultColDef={{
            sortable: true,
            filter: true,
            resizable: true,
            flex: 1,
            editable: false,
          }}
          height="100%"
          containerSx={{ height: '100%' }}
          gridContainerSx={{ height: '100%' }}
          showQuickFilter={false}
          showFooter={false}
          showToolbar={false}
          showExport={false}
          rowHeight={36}
          headerHeight={42}
          suppressHorizontalScroll={false}
          animateRows={false}
          suppressMovableColumns={false}
          suppressColumnVirtualisation={false}
          suppressRowVirtualisation={false}
        />
      </Box>
    </Box>
  );
});

CashflowPanel.displayName = 'CashflowPanel';

export default CashflowPanel;
